import React, { useState, useEffect } from 'react';
import { useAuth, MIN_PASSWORD_LENGTH } from './AuthContext';

// Shown when the app loads from a Supabase password-recovery link (see
// AuthContext.isRecoveryUrl / the PASSWORD_RECOVERY event). Collects a new
// password twice and commits it via updateUser against the recovery session.
function ResetPassword() {
  const { updatePassword, sendPasswordReset } = useAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Set when the recovery link itself is bad/expired — Supabase returns this in
  // the URL hash, and updateUser fails with a missing session. In that state we
  // hide the password form and offer to send a fresh link.
  const [linkError, setLinkError] = useState(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);

  // On mount, read any error the recovery redirect left in the URL hash
  // (e.g. #error=access_denied&error_code=otp_expired), then scrub tokens from
  // the address bar. Recovery state already lives in React by now, so removing
  // the URL markers is safe and keeps the token out of history.
  useEffect(() => {
    const hash = window.location.hash || '';
    if (hash.includes('error')) {
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const code = params.get('error_code') || '';
      if (code.includes('expired') || hash.includes('otp_expired')) {
        setLinkError('This password reset link has expired.');
      } else {
        setLinkError('This password reset link is invalid or has already been used.');
      }
    }
    window.history.replaceState({}, document.title, window.location.origin + '/');
  }, []);

  const looksLikeMissingSession = (msg) =>
    /session|expired|invalid|not\s*found|missing/i.test(msg || '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    // PASSWORD TRANSFORM CONTRACT — commit the credential exactly as typed, with
    // NO trim/normalize/encoding, to stay byte-for-byte symmetric with the login
    // form (Login.js sends it raw to signInWithPassword). If login ever starts
    // trimming, mirror it here too. Do NOT copy Billing-Manager's .trim(): a
    // password set trimmed here would then fail HB's untrimmed login. (See
    // KNOWN_ISSUES.md for the HB-vs-billing divergence.)
    const { error: updateError } = await updatePassword(newPassword);
    setLoading(false);

    if (updateError) {
      // A stale/expired link surfaces here as a missing-session error; route the
      // user to the resend flow rather than a dead-end. Otherwise (e.g. server
      // password-policy rejection) show the server's message verbatim.
      if (looksLikeMissingSession(updateError.message)) {
        setLinkError('This password reset link has expired.');
      } else {
        setError(updateError.message);
      }
      return;
    }
    // Success: updatePassword cleared the recovery flag, so the app re-renders
    // into its normal authenticated entry. Nothing more to do here.
  };

  const handleResend = async (e) => {
    e.preventDefault();
    setResendMessage(null);
    setResending(true);
    try {
      await sendPasswordReset(resendEmail);
    } catch (_) {
      // Swallow — neutral response regardless, to avoid leaking account existence.
    }
    setResending(false);
    setResendMessage('If that account exists, a new reset link is on its way.');
  };

  const boxStyle = {
    backgroundColor: 'var(--surface-1)',
    padding: '3rem',
    borderRadius: 'var(--radius-xl)',
    width: '100%',
    maxWidth: '450px',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid var(--border-default)',
  };
  const labelStyle = {
    display: 'block',
    color: 'var(--text-secondary)',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
  };
  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: 'var(--surface-input)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-primary)',
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const primaryBtnStyle = (busy) => ({
    width: '100%',
    padding: '0.75rem',
    backgroundColor: busy ? 'var(--surface-3)' : 'var(--btn-primary-bg)',
    color: 'var(--text-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: busy ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.2s',
  });
  const noticeStyle = (kind) => ({
    backgroundColor: kind === 'error' ? 'var(--color-danger)' : 'var(--color-success)',
    color: 'var(--text-primary)',
    padding: '0.75rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  });

  return (
    <div className="login-container" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--bg-primary)',
      padding: '2rem',
    }}>
      <div className="login-box" style={boxStyle}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: 'var(--text-primary)', fontSize: '2rem', marginBottom: '0.5rem' }}>
            HM Hauling
          </h1>
          <p style={{ color: 'var(--accent-blue)', fontSize: '1.2rem' }}>
            {linkError ? 'Reset link problem' : 'Set a new password'}
          </p>
        </div>

        {linkError ? (
          <div>
            <div style={noticeStyle('error')}>{linkError}</div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
              Enter your email and we'll send a fresh reset link.
            </p>
            <form onSubmit={handleResend}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelStyle}>Email Address</label>
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  required
                  style={inputStyle}
                  placeholder="your.email@example.com"
                />
              </div>
              {resendMessage && <div style={noticeStyle('success')}>{resendMessage}</div>}
              <button type="submit" disabled={resending} style={primaryBtnStyle(resending)}>
                {resending ? 'Sending...' : 'Send new reset link'}
              </button>
            </form>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={inputStyle}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={inputStyle}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '1rem' }}>
              At least {MIN_PASSWORD_LENGTH} characters.
            </p>

            {error && <div style={noticeStyle('error')}>{error}</div>}

            <button type="submit" disabled={loading} style={primaryBtnStyle(loading)}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
